import {useEffect, useLayoutEffect, useRef} from 'react';
import type {RefCallback, RefObject} from 'react';

const findPortalId = (node: Node | null) => {
    while (node) {
        if (
            node instanceof HTMLElement &&
            node.hasAttribute('data-portal-id')
        ) {
            return node.getAttribute('data-portal-id');
        }
        node = node.parentNode;
    }
    return null;
};

const elementContainsPortalOrigin = (
    element: HTMLElement,
    portalId: string,
) => {
    if (element.getAttribute('data-portal-origin-id') === portalId) {
        return true;
    }

    const matchingChild = element.querySelector(
        `[data-portal-origin-id="${portalId}"]`,
    );
    return matchingChild !== null;
};

/**
 * Hook that only runs callback on click events outside of the referenced element
 *
 * @param fn - the function to be called on click outside
 * @param externalRef - optional external ref to use instead of the internally managed ref
 * @returns a ref to the element where we are listening for clicks
 *
 * @example
 * const ref = useRef<HTMLElement | null>(null);
 * const refCallback = useOnClickOutside(() => close(), ref);
 */
export function useOnClickOutside(
    fn: () => void,
    externalRef?: RefObject<HTMLElement | null>,
) {
    const fnRef = useRef(fn);
    useLayoutEffect(() => {
        fnRef.current = fn;
    }, [fn]);

    const internalRef = useRef<HTMLElement | null>(null);
    const targetRef = externalRef ?? internalRef;

    const refCallback: RefCallback<HTMLElement> = element => {
        if (externalRef) {
            return;
        }

        internalRef.current = element;
    };

    useEffect(() => {
        const handleOutside = (event: MouseEvent | TouchEvent) => {
            if (!targetRef.current || !event.target) {
                return;
            }

            // do nothing if the ref element is clicked
            if (targetRef.current.contains(event.target as Node)) {
                return;
            }

            const portalId = findPortalId(event.target as Node);
            /*
             * If event target is or is an ancestor of a portal element (has data-portal-id=uuid attribute)
             * and the ref element is or contains an element with data-portal-origin-id=uuid
             * which has a matching uuid then we decide that the portal element is "contained/belongs"
             * to ref element (because the origin of the portal is from within ref element) and the
             * click on the portal element is not seen as an outside click, even though
             * it is technically so in terms of DOM structure. This is useful for instance with
             * tooltips that need to be in a portal so they can overflow a container element
             * but visually are within the containing element, in this case clicking on the
             * tooltip should not result in 'outside click' action.
             */
            if (
                portalId &&
                elementContainsPortalOrigin(targetRef.current, portalId)
            ) {
                return;
            }

            // run the callback if something outside is clicked
            fnRef.current();
        };

        document.addEventListener('click', handleOutside, {capture: true});
        document.addEventListener('touchstart', handleOutside, {capture: true});
        return () => {
            document.removeEventListener('click', handleOutside, {
                capture: true,
            });
            document.removeEventListener('touchstart', handleOutside, {
                capture: true,
            });
        };
    }, [targetRef]);

    return refCallback;
}
